import Extent from '@arcgis/core/geometry/Extent';
import { BBox } from 'geojson';
import { isObject } from 'lodash';

import { SnLonLat, SnLonLatLike } from './lon-lat';

export class SnLonLatBounds {
  private readonly _southWest: SnLonLat;
  private readonly _northEast: SnLonLat;

  public static convert(lonLatBoundsLike: SnLonLatBoundsLike): SnLonLatBounds {
    if (lonLatBoundsLike instanceof SnLonLatBounds) {
      return lonLatBoundsLike;
    }

    if (Array.isArray(lonLatBoundsLike) && lonLatBoundsLike.length === 2) {
      return new SnLonLatBounds(lonLatBoundsLike[0], lonLatBoundsLike[1]);
    }

    if (Array.isArray(lonLatBoundsLike) && lonLatBoundsLike.length === 4) {
      const sw: SnLonLatLike = [lonLatBoundsLike[0], lonLatBoundsLike[1]];
      const ne: SnLonLatLike = [lonLatBoundsLike[2], lonLatBoundsLike[3]];

      return new SnLonLatBounds(sw, ne);
    }

    if (!Array.isArray(lonLatBoundsLike) && isObject(lonLatBoundsLike)) {
      const sw: SnLonLatLike = [lonLatBoundsLike.west, lonLatBoundsLike.south];
      const ne: SnLonLatLike = [lonLatBoundsLike.east, lonLatBoundsLike.north];

      return new SnLonLatBounds(sw, ne);
    }
  }

  public static fromGeojsonBBox(bbox: BBox): SnLonLatBounds {
    const sw: SnLonLatLike = [bbox[0], bbox[1]];
    const ne: SnLonLatLike = [bbox[2], bbox[3]];

    return new SnLonLatBounds(sw, ne);
  }

  public static convertToLatLng(lonLatBounds: SnLonLatBounds): google.maps.LatLngBounds {
    const ne = new google.maps.LatLng(lonLatBounds._northEast.lat, lonLatBounds._northEast.lon);
    const sw = new google.maps.LatLng(lonLatBounds._southWest.lat, lonLatBounds._southWest.lon);
    return new google.maps.LatLngBounds(sw, ne);
  }

  public static convertToExtent(lonLatBounds: SnLonLatBounds): Extent {
    return new Extent({
      xmin: lonLatBounds._southWest.lon,
      ymin: lonLatBounds._southWest.lat,
      xmax: lonLatBounds._northEast.lon,
      ymax: lonLatBounds._northEast.lat,
      spatialReference: { wkid: 4326 },
    });
  }

  constructor(sw: SnLonLatLike, ne: SnLonLatLike) {
    this._southWest = SnLonLat.convert(sw);
    this._northEast = SnLonLat.convert(ne);
  }

  public getSouthWest(): SnLonLat {
    return this._southWest;
  }

  public getNorthEast(): SnLonLat {
    return this._northEast;
  }

  public getNorthWest(): SnLonLat {
    return new SnLonLat(this.getWest(), this.getNorth());
  }

  public getSouthEast(): SnLonLat {
    return new SnLonLat(this.getEast(), this.getSouth());
  }

  public getWest(): number {
    return this._southWest.lon;
  }

  public getSouth(): number {
    return this._southWest.lat;
  }

  public getEast(): number {
    return this._northEast.lon;
  }

  public getNorth(): number {
    return this._northEast.lat;
  }

  public getCenter(): SnLonLat {
    const lat = (this._southWest.lat + this._northEast.lat) / 2;
    const lon = (this._southWest.lon + this._northEast.lon) / 2;

    return new SnLonLat(lon, lat);
  }

  public extend(obj: SnLonLat | SnLonLatBounds): this {
    const sw = this._southWest;
    const ne = this._northEast;

    let sw2;
    let ne2;

    if (obj instanceof SnLonLat) {
      sw2 = obj.clone();
      ne2 = obj.clone();
    } else if (obj instanceof SnLonLatBounds) {
      sw2 = obj.getSouthWest().clone();
      ne2 = obj.getNorthEast().clone();
    }

    sw.lat = Math.min(sw2.lat, sw.lat);
    sw.lon = Math.min(sw2.lon, sw.lon);

    ne.lat = Math.max(ne2.lat, ne.lat);
    ne.lon = Math.max(ne2.lon, ne.lon);

    return this;
  }

  public toArray(): [[number, number], [number, number]] {
    return [this._southWest.toArray(), this._northEast.toArray()];
  }

  public toString(): string {
    return `(${this._southWest.toString()}, ${this._northEast.toString()})`;
  }

  public toUrlValue(): string {
    return `${this._southWest.toUrlValue()},${this._northEast.toUrlValue()}`;
  }

  public toJSON(): ISnLonLatBoundsLiteral {
    return {
      south: this.getSouth(),
      west: this.getWest(),
      north: this.getNorth(),
      east: this.getEast(),
    };
  }

  public toBbox(): [number, number, number, number] {
    return [this.getWest(), this.getSouth(), this.getEast(), this.getNorth()];
  }

  public getRadiusInMeters(): number {
    const latDiff = this.getNorth() - this.getSouth();
    const lonDiff = this.getEast() - this.getWest();
    const diagonalDistance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);

    // Convert to meters (approximate conversion for small distances)
    // 1 degree ≈ 111km
    return diagonalDistance * 111000;
  }
}

export interface ISnLonLatBoundsLiteral {
  south: number;
  west: number;
  north: number;
  east: number;
}

export type SnLonLatBoundsLike =
  | SnLonLatBounds
  | ISnLonLatBoundsLiteral
  | [SnLonLatLike, SnLonLatLike]
  | [number, number, number, number];
